// v10/src/components/nav/NavigationMap.js
//
// Mapa Leaflet con polyline de la ruta + markers en cada waypoint.

import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix marker icons (Leaflet default icons no funcionan con webpack/CRA)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

/**
 * Ajusta el viewport del mapa para que entren todos los waypoints.
 */
const FitBounds = ({ waypoints }) => {
  const map = useMap();
  useEffect(() => {
    if (!map || !waypoints || waypoints.length < 1) return;
    const points = waypoints.filter(w => w && Number.isFinite(w.lat) && Number.isFinite(w.lng));
    if (!points.length) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 8);
      return;
    }
    const bounds = L.latLngBounds(points.map(w => [w.lat, w.lng]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 10 });
  }, [waypoints, map]);
  return null;
};

/**
 * Mapa con polyline de la ruta.
 *
 * Props:
 *   - waypoints: Array<{ icao, name, lat, lng }> en orden (origen → ... → destino)
 *   - height: string (default '400px')
 *   - darkMode: bool
 */
const NavigationMap = ({ waypoints, height = '400px', darkMode }) => {
  const validWaypoints = (waypoints || []).filter(
    w => w && Number.isFinite(Number(w.lat)) && Number.isFinite(Number(w.lng))
  );

  // Sin waypoints: mostrar centro de Argentina
  if (validWaypoints.length === 0) {
    return (
      <MapContainer
        center={[-38, -64]}
        zoom={4}
        style={{ width: '100%', height, borderRadius: 12 }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />
      </MapContainer>
    );
  }

  const center = [Number(validWaypoints[0].lat), Number(validWaypoints[0].lng)];
  const polylinePositions = validWaypoints.map(w => [Number(w.lat), Number(w.lng)]);

  return (
    <MapContainer
      center={center}
      zoom={6}
      style={{ width: '100%', height, borderRadius: 12 }}
      scrollWheelZoom={false}
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
      />

      {validWaypoints.map((w, idx) => (
        <Marker key={`${w.icao || 'wp'}-${idx}`} position={[Number(w.lat), Number(w.lng)]}>
          <Popup>
            <strong>{w.icao}</strong>
            <br />
            {w.name}
            <br />
            {idx === 0
              ? '✈️ Origen'
              : idx === validWaypoints.length - 1
                ? '🎯 Destino'
                : `📍 Waypoint ${idx}`}
          </Popup>
        </Marker>
      ))}

      {validWaypoints.length >= 2 && (
        <Polyline
          positions={polylinePositions}
          pathOptions={{
            color: darkMode ? '#60a5fa' : '#2563eb',
            weight: 3,
            opacity: 0.85,
          }}
        />
      )}

      <FitBounds waypoints={validWaypoints} />
    </MapContainer>
  );
};

export default NavigationMap;
